import { DataLoadError, DataView, DataViewConfig, Divider, Loader, Pagination } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { isNil } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import React, { useLayoutEffect, useState } from 'react';
import NoResults from '../../../components/NoResults';
import { useFetchPaginatedArticles } from '../../../hooks/articles';
import { ContentTypeFilter, FeedType, Filters } from '../../../models';
import { ArticleTableRow } from '../../../models/ArticleTableRow';
import styles from './ArticleList.module.scss';

const cx = classNames.bind(styles);

const DEFAULT_ARTICLE_COUNT = 100;

interface ArticleListProps {
  viewConfig: DataViewConfig<ArticleTableRow>;
  expanded?: boolean;
  issuerTag?: string | undefined;
  feedType?: FeedType;
  setHasResults?: (value: boolean) => void;
  filters?: Filters | null;
  articleCount?: number;
  previewArticleId?: number | null;
  page: number;
  setPage: (value: number) => void;
  contentTypeFilter: ContentTypeFilter;
}

const getKey = (article: ArticleTableRow) => article.id;

const ArticleList = ({
  viewConfig,
  issuerTag,
  previewArticleId,
  feedType = FeedType.Global,
  expanded = false,
  setHasResults,
  filters = null,
  articleCount = DEFAULT_ARTICLE_COUNT,
  page,
  setPage,
  contentTypeFilter,
}: ArticleListProps): React.ReactElement | null => {
  const [articles, setArticles] = useState<ArticleTableRow[]>();
  const { data, totalPages, isFetching, isError, refetch } = useFetchPaginatedArticles(
    feedType,
    articleCount,
    page,
    filters,
    issuerTag
  );
  const isLoading = isFetching || (!articles && !isError) || !filters;

  const onPageChange = (page: number) => {
    setPage(page);
    window.scrollTo({ top: 0 });
  };

  useLayoutEffect(() => {
    setArticles(undefined);
  }, [contentTypeFilter, page]);

  useLayoutEffect(() => {
    if (data) {
      setArticles(data);
      setHasResults?.(!isEmpty(data));
    }
  }, [data, filters]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={cx('state-wrapper')}>
          <Loader position="center" />
        </div>
      );
    }
    if (isError) {
      return (
        <div className={cx('state-wrapper')}>
          <DataLoadError onRetryClick={refetch} />
        </div>
      );
    }
    if (!articles || isEmpty(articles)) {
      return (
        <div className={cx('state-wrapper')}>
          <NoResults />
        </div>
      );
    }
    return (
      <DataView
        data={articles}
        config={viewConfig}
        getKey={getKey}
        expanded={expanded}
        activeKeys={!isNil(previewArticleId) ? Array.of(previewArticleId) : undefined}
      />
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1 || isLoading || isError) {
      return null;
    }

    return (
      <div className={cx('footer')} data-testid="article-list-footer">
        <Divider />
        <Pagination activePage={page} onPageChange={onPageChange} totalPages={totalPages} />
      </div>
    );
  };

  return (
    <div
      className={cx('body', { 'is-expanded': isLoading || isEmpty(articles) || isError })}
      data-testid="article-list"
    >
      {renderContent()}
      {renderPagination()}
    </div>
  );
};

export default ArticleList;
